import { Actor, CollisionType, Color, Engine, Scene, SceneActivationContext, Shader, vec, Vector } from 'excalibur';
import { Images } from './resources';
import { EventsManager } from '@game-libs/events-manager';
import { EntityUpgradesList, GameGenerators, GUIActions, UpgradeType } from '../types/game-types';
import { useGeneratorsStore } from '../store/zustand/useGeneratorsStore';
import { ShakeObjectComponent } from '@game-libs/exc-libs/src/components';
import { GameplayEvents } from '../types/game-events';
import { Bird } from './bird';
import { Unit } from './unit';
import { sublinearGrowth } from '@game-libs/exc-libs/src/helpers/math.helper';
import { getMaterial, getRandomVector, Shaders } from '@game-libs/exc-libs';
import { BirdTrackingSystem } from './birdTrackingSystem';
import { kingAnimation, lightningAnimation, poisonAnimation } from './animations';

export abstract class BaseLevel extends Scene {
  protected shake: ShakeObjectComponent;
  protected initialCameraPosition: Vector = vec(0, 0);
  protected middlePositionReference: Vector = vec(0, 0);
  public birdTrackingSystem!: BirdTrackingSystem;
  protected initialCameraZoom: number = 1;

  protected unitsCount = {
    warrior: 0,
    wizard: 0,
    thief: 0,
    elf: 0,
    cleric: 0,
    bard: 0,
    catapult: 0,
  };

  // Event listener unsubscribe functions
  protected unsubToggleUI?: () => void;
  protected unsubPurchasedUpgrade?: () => void;
  protected unsubCreateGameBird?: () => void;
  protected unsubInitGame?: () => void;
  protected unsubTick?: () => void;
  protected unsubLightningDamage?: () => void;
  protected unsubPoisonAttack?: () => void;

  constructor() {
    super();

    this.shake = new ShakeObjectComponent({
      decay: 1.2,
      maxOffset: 10,
      maxRotation: 0.04,
      frequency: 7,
    });
  }

  public getOffsetPosition(offset?: Vector): Vector {
    return this.middlePositionReference.add(offset || vec(0, 0));
  }

  /**
   * Returns the position where attacks should be targeted.
   * Subclasses can override to customize attack positioning.
   */
  public getAttackTargetPosition(offset = vec(0, 0)): Vector {
    return this.getOffsetPosition(vec(0, 50)).add(offset);
  }

  public getScreenSizeRatio(engine: Engine): number {
    return engine.screen.scaledHeight / 1080;
  }

  protected updateMiddlePosition(engine: Engine) {
    // this.middlePositionReference = engine.screenToWorldCoordinates(
    //   vec(engine.halfCanvasWidth, engine.canvasHeight / 2.7),
    // );

    this.middlePositionReference = vec(engine.halfCanvasWidth, engine.canvasHeight / 2.7);

  }

  override onInitialize(engine: Engine): void {
    this.camera.y = engine.halfCanvasHeight - 200;
    this.camera.x = engine.halfCanvasWidth;
    this.camera.zoom = 1.5;


    this.updateMiddlePosition(engine);

    const { on } = EventsManager();

    // Setup background
    const gameBKG = new Actor({
      pos: this.getOffsetPosition(vec(0, -30)),
      width: 1000,
      height: 600,
      color: Color.Blue,
      collisionType: CollisionType.PreventCollision,
    });

    const bkgMaterial = getMaterial(engine, Shaders.PERSPECTIVE, Images.GameBKG);
    gameBKG.graphics.material = bkgMaterial;

    bkgMaterial.update((shader: Shader) => {
      if (shader) {
        shader.trySetUniformFloat('u_perspective_strength', 0.45);
      }
    });

    this.add(gameBKG);

    const king = new Actor({
      pos: this.getOffsetPosition(vec(-250, -210)),
      width: 8,
      height: 8,
      color: Color.Blue,
      collisionType: CollisionType.PreventCollision,
    });
    king.graphics.use(kingAnimation.idle.clone());
    this.add(king);
    king.scale = vec(2.5, 2.5);

    king.on('pointerdown', () => {
      king.graphics.use(kingAnimation.shake_head.clone());
    });


    // Initialize bird tracking system
    this.birdTrackingSystem = new BirdTrackingSystem(this.world, 200);
    this.world.add(this.birdTrackingSystem);

    // Bird/upgrade handlers
    this.unsubPurchasedUpgrade = on(GameplayEvents.PURCHASED_UPGRADE, ({ id, multiplier }) => {
      if (id === UpgradeType.HuntingSeason) {
        setTimeout(() => {
          for (let i = 0; i < multiplier; i++) {
            this.add(new Bird());
          }
        }, 10);
      }
    });

    this.unsubCreateGameBird = on(GameplayEvents.CREATE_GAME_BIRD, () => {
      this.add(new Bird());
    });

    const unitTypes: ('warrior' | 'wizard' | 'thief' | 'elf' | 'cleric' | 'bard' | 'catapult')[] = [
      'warrior',
      'wizard',
      'thief',
      'elf',
      'cleric',
      'bard',
      'catapult',
    ];

    // this.unsubInitGame = on(GUIActions.INIT_GAME, ({ generators }) => {
    //   this.createUnits(unitTypes, generators);
    // });

    // Generators live in Zustand; level start always reads the current store snapshot.
    this.unsubInitGame = on(GUIActions.START_LEVEL, () => {
      const generators = useGeneratorsStore.getState().generators;
      this.createUnits(unitTypes, generators);
    });

    this.unsubTick = on(
      GUIActions.TICK,
      ({ payload, upgrades }: { payload: GameGenerators; upgrades?: EntityUpgradesList }) => {
        this.createUnits(unitTypes, payload);

        // HuntersEye upgrade: kill one bird per tick if upgrade is active
        if (upgrades?.elf?.huntersEye?.purchased && upgrades.elf.huntersEye.purchased > 0) {
          const birdsInRange = this.birdTrackingSystem.getBirdsInRange();
          if (birdsInRange.length > 0) {
            if (Math.random() <= upgrades.elf.huntersEye.multiplier / 100) {
              birdsInRange[0].killBird();
            }
          }
        }
      },
    );

    this.initialCameraPosition = vec(this.camera.x, this.camera.y);

    // Attack effect handlers
    this.unsubLightningDamage = on(GameplayEvents.LIGHTNING_DAMAGE_DRAGON, () => {
      this.showLightningAttack();
    });

    this.unsubPoisonAttack = on(GameplayEvents.POISON_ATTACK_DRAGON, () => {
      this.showPoisonAttack();
    });

    // Call hook for subclass-specific initialization
    this.onLevelInitialize(engine);
  }

  /**
   * Hook for subclasses to add their own initialization logic.
   * Called at the end of onInitialize.
   */
  protected abstract onLevelInitialize(engine: Engine): void;

  protected createUnits(
    unitTypes: ('warrior' | 'wizard' | 'elf' | 'thief' | 'cleric' | 'bard' | 'catapult')[],
    payload: GameGenerators,
  ) {
    // Collect all units to create, then stagger their creation
    const unitsToCreate: { type: (typeof unitTypes)[number]; count: number }[] = [];

    unitTypes.forEach((unitType) => {
      const count = this.unitsCount[unitType];
      const toGenerate = Math.min(60, sublinearGrowth(payload[unitType].owned, 3.7)) - count;
      if (toGenerate > 0) {
        this.unitsCount[unitType] += toGenerate;
        unitsToCreate.push({ type: unitType, count: toGenerate });
      }
    });

    // Stagger unit creation over multiple frames to avoid performance spikes
    // Create max 3 units per frame
    const MAX_UNITS_PER_FRAME = 3;
    let unitIndex = 0;

    const createNextBatch = () => {
      let created = 0;
      while (unitIndex < unitsToCreate.length && created < MAX_UNITS_PER_FRAME) {
        const unitInfo = unitsToCreate[unitIndex];
        if (unitInfo.count > 0) {
          this.add(new Unit(unitInfo.type, this.unitsCount[unitInfo.type]));
          unitInfo.count--;
          created++;
        }
        if (unitInfo.count === 0) {
          unitIndex++;
        }
      }

      // Schedule next batch if there are more units to create
      if (unitIndex < unitsToCreate.length) {
        requestAnimationFrame(createNextBatch);
      }
    };

    if (unitsToCreate.length > 0) {
      createNextBatch();
    }
  }

  showLightningAttack() {
    const lightning = new Actor({
      width: 111,
      height: 159,
      pos: this.getAttackTargetPosition(),
      anchor: vec(0.5, 0.9),
      z: 10000,
      collisionType: CollisionType.PreventCollision,
    });
    lightning.scale = vec(2, 2);
    lightning.graphics.use(lightningAnimation.clone());

    this.add(lightning);

    // Call hook for additional lightning effects (e.g., enemy blink)
    setTimeout(() => {
      this.onLightningAttackHit();
    }, 200);

    // Kill after animation plays
    setTimeout(() => {
      lightning.kill();
    }, 600);
  }

  /**
   * Hook for subclasses to add additional effects when lightning hits.
   */
  protected onLightningAttackHit(): void {
    // Override in subclass if needed
  }

  showPoisonAttack() {
    const poison = new Actor({
      width: 48,
      height: 48,
      pos: this.getAttackTargetPosition(getRandomVector(60).add(vec(0, -60))),
      anchor: vec(0.5, 1.1),
      z: 10000,
      collisionType: CollisionType.PreventCollision,
    });
    poison.scale = vec(2, 2);
    poison.graphics.use(poisonAnimation.clone());

    this.add(poison);

    // Call hook for additional poison effects (e.g., enemy blink)
    this.onPoisonAttackHit();

    // Kill after animation plays
    setTimeout(() => {
      poison.kill();
    }, 600);
  }

  override onPreUpdate(engine: Engine, elapsedMs: number): void {
    // this.updateMiddlePosition(this.engine);
    this.shake.update(elapsedMs / 1000);

    const { offset, rotation } = this.shake.getShake();

    this.camera.x = this.initialCameraPosition.x + offset.x;
    this.camera.y = this.initialCameraPosition.y + offset.y;
    this.camera.rotation = rotation;
  }

  protected onPoisonAttackHit(): void {
    // Override in subclass if needed
  }

  public onDeactivate(ctx: SceneActivationContext) {
    // Unsubscribe from base event listeners
    this.unsubToggleUI?.();
    this.unsubPurchasedUpgrade?.();
    this.unsubCreateGameBird?.();
    this.unsubInitGame?.();
    this.unsubTick?.();
    this.unsubLightningDamage?.();
    this.unsubPoisonAttack?.();

    // Call hook for subclass cleanup
    this.onLevelDeactivate(ctx);
  }

  /**
   * Hook for subclasses to add their own cleanup logic.
   * Called at the end of onDeactivate.
   */
  protected abstract onLevelDeactivate(ctx: SceneActivationContext): void;
}
